import { cn } from '@risksmart-app/atomic-ui';
import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types/risk-assessment-result-config';

import { PreviewCell } from './PreviewCell';

type RatingLevel = RiskAssessmentResultConfig['impact']['ratings'][number];

type RatingsPreviewProps = {
  config: RiskAssessmentResultConfig;
  likelihoodLabel: string;
  impactLabel: string;
};

const RatingsPreview = ({
  config,
  likelihoodLabel,
  impactLabel,
}: RatingsPreviewProps) => {
  const { likelihood, impact } = config;
  const columns = Math.max(likelihood.ratings.length, impact.ratings.length);

  const renderRow = (
    key: string,
    label: string,
    levels: RatingLevel[],
    labelColor?: string
  ) => {
    const padCount = columns - levels.length;

    return (
      <div key={key} className={cn('contents')}>
        <PreviewCell label={label} color={labelColor} />
        {levels.map((level) => (
          <PreviewCell
            key={level.value}
            color={level.color}
            label={level.title}
            value={level.value}
          />
        ))}
        {Array.from({ length: padCount }, (_, i) => (
          <div key={`pad-${i}`} />
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn('grid w-full gap-2')}
      style={{
        gridTemplateColumns: `repeat(${columns + 1}, minmax(max-content, 1fr))`,
      }}
    >
      {renderRow('likelihood', likelihoodLabel, likelihood.ratings)}
      <div
        className={cn('my-1 border-t border-neutral-border')}
        style={{ gridColumn: '1 / -1' }}
      />
      {impact.categories.length > 0
        ? impact.categories.map((c, index) =>
            renderRow(
              `impact-${index}-${c.name}`,
              c.name,
              impact.ratings,
              c.color
            )
          )
        : renderRow('impact', impactLabel, impact.ratings)}
    </div>
  );
};

export { RatingsPreview };
